import pin from '@assets/images/google-map/pin.webp';
import {
  defaultJobListFilter,
  type JobListFilter,
  jobMatchesListFilter,
} from '@features/jobs';
import {
  JobMarker,
  type MapJob,
  MapLocationPicker,
  Popover,
} from '@features/r8tr/map/google-map';
import { useAvailableJobsQuery, useCurrentJobsQuery } from '@lib/api/jobs';
import { useMakeAddressDefaultMutation, useProfileQuery } from '@lib/api/profile';
import { useAppSelector } from '@lib/store/hooks';
import Box from '@mui/material/Box';
import GoogleMapReact, {
  type GoogleCircle,
  type GoogleMapHandle,
  type GoogleMapsNamespace,
} from 'google-map-react';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GOOGLE_MAPS_API_KEY, googleMapThemeOptions } from './googleMapTheme';
import type { ProfileResponse } from './googleMapTypes';

const themeRgb = (name: string): string => {
  const channels = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return `rgb(${channels})`;
};

const markerStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  color: 'red',
  height: '30px',
  transform: 'translate(-50%, -100%)',
};

interface GoogleMapsProps {
  showCurrentJobs?: boolean;
  jobFilter?: JobListFilter;
}

const GoogleMaps = ({
  showCurrentJobs,
  jobFilter = defaultJobListFilter,
}: GoogleMapsProps): JSX.Element => {
  const { t } = useTranslation();
  const mode = useAppSelector((state) => state.colorMode.mode);
  const navigator = useNavigate();
  const makeDefaultAddress = useMakeAddressDefaultMutation();
  const { data: profileResult = {} } = useProfileQuery();
  const userDetails = profileResult as ProfileResponse;
  const areas = userDetails.data?.areas ?? [];
  const selectedArea = areas.find((area) => area.isDefault) || areas[0];
  const [mapData, setMapData] = useState<MapJob[]>([]);
  const [PopoverData, setPopoverData] = useState<MapJob | null>(null);
  const [circleRadius, setCircleRadius] = useState(selectedArea?.radius);
  const mapRef = useRef<GoogleMapHandle | null>(null);
  const mapsRef = useRef<GoogleMapsNamespace | null>(null);
  const circleRef = useRef<GoogleCircle | null>(null);

  const { data: availableJobsResult = [] } = useAvailableJobsQuery();
  const { data: currentJobsResult = [] } = useCurrentJobsQuery();
  const availableJobsData = availableJobsResult as MapJob[];
  const currentJobsData = currentJobsResult as MapJob[];

  useEffect(() => {
    setCircleRadius(selectedArea?.radius);
  }, [selectedArea?.radius]);

  const handleDefaultAddress = (id: string | number): void => {
    if (!id || String(id) === String(selectedArea?.id)) {
      return;
    }
    makeDefaultAddress.mutate(id as string);
  };

  useEffect(() => {
    const source = showCurrentJobs ? currentJobsData : availableJobsData;
    setMapData(source.filter((job) => jobMatchesListFilter(job, jobFilter)));
  }, [availableJobsData, currentJobsData, jobFilter, showCurrentJobs]);

  const handlePopover = (data: MapJob): void => {
    if (PopoverData?.id != data.id) {
      setPopoverData(data);
    }
  };

  const handleOnMouseOut = (): void => {
    setPopoverData(null);
  };

  const mapLatitude = selectedArea?.latitude;
  const mapLongitude = selectedArea?.longitude;
  const hasValidCenter =
    typeof mapLatitude === 'number' && typeof mapLongitude === 'number';
  const mapCenter = {
    lat: mapLatitude,
    lng: mapLongitude,
  };

  useEffect(() => {
    if (
      !mapRef.current ||
      !mapsRef.current ||
      typeof mapLatitude !== 'number' ||
      typeof mapLongitude !== 'number'
    ) {
      return;
    }

    if (!circleRef.current) {
      circleRef.current = new mapsRef.current.Circle({
        strokeColor: themeRgb('--primary'),
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: themeRgb('--primary'),
        fillOpacity: 0.12,
        map: mapRef.current,
      });
    }

    circleRef.current.setCenter({ lat: mapLatitude, lng: mapLongitude });
    circleRef.current.setRadius(circleRadius ? circleRadius * 1000 : 0);
  }, [circleRadius, hasValidCenter, mapCenter.lat, mapCenter.lng]);

  return (
    <Box
      sx={{
        position: 'relative',
        top: 0,
        bottom: 0,
        zIndex: 0,
        height: '100%',
        minHeight: 280,
        width: '100%',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 5,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <MapLocationPicker
          areas={areas}
          selectedId={selectedArea?.id}
          onSelect={handleDefaultAddress}
        />
      </Box>
      {typeof mapLatitude === 'number' && typeof mapLongitude === 'number' ? (
        <GoogleMapReact
          key={mode}
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
          }}
          center={{ lat: mapLatitude, lng: mapLongitude }}
          defaultZoom={11}
          options={(maps) => googleMapThemeOptions(mode, maps)}
          yesIWantToUseGoogleMapApiInternals={true}
          onGoogleApiLoaded={({ map, maps }) => {
            mapRef.current = map;
            mapsRef.current = maps;
            circleRef.current = new maps.Circle({
              strokeColor: themeRgb('--primary'),
              strokeOpacity: 0.6,
              strokeWeight: 2,
              fillColor: themeRgb('--primary'),
              fillOpacity: 0.12,
              map,
            });
            circleRef.current.setCenter({
              lat: mapLatitude,
              lng: mapLongitude,
            });
            circleRef.current.setRadius(circleRadius ? circleRadius * 1000 : 0);
          }}
        >
          {mapData.map((job) => {
            return (
              <JobMarker
                key={job.id}
                lat={job.latitude}
                lng={job.longitude}
                onClick={() => {
                  navigator('/detail', { state: { jobId: job.id } });
                }}
              >
                <img
                  src={pin}
                  alt={t('common.pinAlt')}
                  style={markerStyle}
                  onMouseOver={() => handlePopover(job)}
                  onMouseOut={handleOnMouseOut}
                />
              </JobMarker>
            );
          })}
          {PopoverData != null && (
            <Popover
              lat={PopoverData.latitude}
              lng={PopoverData.longitude}
              data={PopoverData}
            />
          )}
        </GoogleMapReact>
      ) : (
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'text.secondary',
          }}
        >
          {t('common.loadingMap')}
        </Box>
      )}
    </Box>
  );
};
export default GoogleMaps;
